import { CryptoError } from './errors.js'

const HEX_ALPHABET = '0123456789abcdef'
const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

const BASE64URL_LOOKUP: ReadonlyMap<string, number> = new Map(
  [...BASE64URL_ALPHABET].map((char, index) => [char, index]),
)

/**
 * WebCrypto's BufferSource excludes SharedArrayBuffer-backed views. All byte
 * arrays produced or accepted by this package are ordinary in-memory views, so
 * this narrows the type at the call boundary without copying.
 */
export function toBufferSource(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return bytes as Uint8Array<ArrayBuffer>
}

export function bytesToHex(bytes: Uint8Array): string {
  let out = ''
  for (const byte of bytes) {
    out += HEX_ALPHABET[byte >> 4]
    out += HEX_ALPHABET[byte & 0x0f]
  }
  return out
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'hexToBytes requires an even-length hex string')
  }
  const bytes = new Uint8Array(hex.length / 2)
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16)
  }
  return bytes
}

/** Base64url without padding (binary fields omit padding — PRD §7.4). */
export function bytesToBase64Url(bytes: Uint8Array): string {
  let out = ''
  for (let index = 0; index < bytes.length; index += 3) {
    const b0 = bytes[index] ?? 0
    const b1 = bytes[index + 1]
    const b2 = bytes[index + 2]
    out += BASE64URL_ALPHABET[b0 >> 2]
    out += BASE64URL_ALPHABET[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)]
    if (b1 === undefined) break
    out += BASE64URL_ALPHABET[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)]
    if (b2 === undefined) break
    out += BASE64URL_ALPHABET[b2 & 0x3f]
  }
  return out
}

export function base64UrlToBytes(text: string): Uint8Array {
  if (text.length % 4 === 1 || !/^[A-Za-z0-9_-]*$/.test(text)) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'base64url value is malformed')
  }
  const bytes = new Uint8Array(Math.floor((text.length * 3) / 4))
  let index = 0
  let buffer = 0
  let bits = 0
  for (const char of text) {
    const value = BASE64URL_LOOKUP.get(char)
    if (value === undefined) {
      throw new CryptoError('CRYPTO_INVALID_INPUT', 'base64url value contains an invalid character')
    }
    buffer = (buffer << 6) | value
    bits += 6
    if (bits >= 8) {
      bits -= 8
      bytes[index] = (buffer >> bits) & 0xff
      index += 1
    }
  }
  return bytes.slice(0, index)
}
